import React from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { getPathname } from 'utils/helpers';
import { parseCustomChatViewIdFromPathname } from 'utils/chat-path-params';

export type ChatAudience = 'mine' | 'team';

export type ChatViewId =
  | 'all'
  | 'my'
  | 'mySnoozed'
  | 'completed'
  | 'notMy'
  | 'unassigned'
  | 'bots'
  | 'custom';

export type ChatViewUsersId =
  | 'allChatUsers'
  | 'myChatUsers'
  | 'mySnoozedChatUsers'
  | 'notMyChatUsers'
  | 'unassignedChatUsers'
  | 'botsChatUsers'
  | 'customChatUsers'
  | 'completedChatUsers';

/** מפתח Redux לרשימת שיחות לפי תצוגה */
export function chatViewIdToReduxUsersKey(viewId: ChatViewId): ChatViewUsersId {
  if (viewId === 'mySnoozed') return 'mySnoozedChatUsers';
  return `${viewId}ChatUsers` as ChatViewUsersId;
}

/** מיפוי ל-getChatUsers: איזה view לשלוח ל-API ואיזה מפתח areUsersRequested לבדוק */
export function getChatViewFetchTarget(viewId: ChatViewId): {
  apiView: ChatViewId;
  requestedKey: ChatViewUsersId;
} {
  if (viewId === 'mySnoozed') return { apiView: 'mySnoozed', requestedKey: 'mySnoozedChatUsers' };
  return { apiView: viewId, requestedKey: chatViewIdToReduxUsersKey(viewId) };
}

export const useCurrentChatView: () => {
  id: ChatViewId;
  path: string;
  viewId?: string;
  audience: ChatAudience;
} = () => {
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const relevantPath = React.useMemo(() => getRelevantPath(pathname), [pathname]);
  const audience: ChatAudience = searchParams.get('audience') === 'team' ? 'team' : 'mine';

  const customViewIdFromPath = React.useMemo(() => parseCustomChatViewIdFromPathname(pathname), [pathname]);

  const view = React.useMemo(() => {
    const v = getCurrentView(relevantPath);
    if (v.id === 'custom' && customViewIdFromPath) {
      return { ...v, viewId: customViewIdFromPath };
    }
    return v;
  }, [relevantPath, customViewIdFromPath]);

  return React.useMemo(() => {
    const base = view || { id: 'all' as const, path: '/chats' };
    return { ...base, audience };
  }, [view, audience]);
};

export const getCurrentView: (pathname?: string) => {
  id: ChatViewId;
  path: string;
  /** מזהה התצוגה המותאמת — חובה להעביר ל-getChatUsers, אחרת פילטרי התצוגה לא נשלחים */
  viewId?: string;
} = (pathname) => {
  const pathnameReal = pathname || getPathname();

  if (pathnameReal.startsWith('/chats/custom')) {
    const customMatch = pathnameReal.match(/^\/chats\/custom\/([^/]+)/);
    if (customMatch) {
      return { id: 'custom', path: `/chats/custom/${customMatch[1]}`, viewId: customMatch[1] };
    }
    return { id: 'custom', path: '/chats/custom' };
  }

  if (pathnameReal.startsWith('/chats/my/snoozed')) {
    return { id: 'mySnoozed', path: '/chats/my/snoozed' };
  }
  if (pathnameReal.startsWith('/chats/my')) {
    return { id: 'my', path: '/chats/my' };
  }

  if (pathnameReal.startsWith('/chats/completed')) {
    return { id: 'completed', path: '/chats/completed' };
  }

  if (pathnameReal.startsWith('/chats/not-my')) return { id: 'notMy', path: '/chats/not-my' };
  if (pathnameReal.startsWith('/chats/unassigned')) return { id: 'unassigned', path: '/chats/unassigned' };
  if (pathnameReal.startsWith('/chats/bots')) return { id: 'bots', path: '/chats/bots' };

  return { id: 'all', path: '/chats' };
};

export const getCurrentViewPathById = (viewId: ChatViewId): string => {
  switch (viewId) {
    case 'my':
      return '/chats/my';
    case 'mySnoozed':
      return '/chats/my/snoozed';
    case 'completed':
      return '/chats/completed';
    case 'notMy':
      return '/chats/not-my';
    case 'unassigned':
      return '/chats/unassigned';
    case 'bots':
      return '/chats/bots';
    case 'custom':
      return '/chats/custom';
    default:
      return '/chats';
  }
};

function getRelevantPath(pathname: string): string {
  const customMatch = pathname.match(/^\/chats\/custom\/([^/]+)/);
  if (customMatch) {
    return `/chats/custom/${customMatch[1]}`;
  }

  if (pathname.startsWith('/chats/my/snoozed')) {
    return '/chats/my/snoozed';
  }
  if (pathname.startsWith('/chats/completed')) {
    return '/chats/completed';
  }

  const match = pathname.match(/^\/chats\/(my|not-my|unassigned|bots|custom)(\/|$)/);
  if (match) {
    return `/chats/${match[1]}`;
  }
  return '/chats';
}
